import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, ShoppingCart, Menu, X, Leaf, Send, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useCart } from '../../context/CartContext';
import { companyInfo } from '../../data/mock';

// Хелпер для навигации с прокруткой вверх
const scrollToTop = () => {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { getCartCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const cartCount = getCartCount();

  const navLinks = [
    { path: '/', label: 'Главная' },
    { path: '/catalog', label: 'Каталог' },
    { path: '/promotions', label: 'Акции' },
    { path: '/delivery', label: 'Доставка' },
    { path: '/custom', label: 'Фермерские' },
    { path: '/contacts', label: 'Контакты' }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Навигация с прокруткой вверх
  const handleNavClick = (e, path) => {
    e.preventDefault();
    scrollToTop();
    setIsMenuOpen(false);
    navigate(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-emerald-100 shadow-sm">
      {/* Top bar */}
      <div className="bg-emerald-800 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <span className="hidden sm:block">{companyInfo.workHours}</span>
          <div className="flex items-center gap-4">
            <a href={`tel:${companyInfo.phoneClean}`} className="flex items-center gap-2 hover:text-emerald-200 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-medium">{companyInfo.phone}</span>
            </a>
            <div className="hidden md:flex items-center gap-2">
              <a 
                href={companyInfo.social.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-emerald-200 transition-colors"
                title="WhatsApp"
              >
                <MessageCircle className="w-4 h-4" />
              </a>
              <a 
                href={companyInfo.social.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-emerald-200 transition-colors"
                title="Telegram"
              >
                <Send className="w-4 h-4" />
              </a>
              <a 
                href={companyInfo.social.max}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-emerald-200 transition-colors text-xs font-bold"
                title="MAX"
              >
                M
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" onClick={(e) => handleNavClick(e, '/')} className="flex items-center gap-3 group">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-emerald-200 transition-shadow">
              <Leaf className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-emerald-800 leading-tight">{companyInfo.name}</h1>
              <p className="text-xs text-emerald-600">Теплицы в {companyInfo.regionPredl}</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={(e) => handleNavClick(e, link.path)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Link to="/cart" onClick={(e) => handleNavClick(e, '/cart')} className="relative">
              <Button variant="outline" size="icon" className="border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300">
                <ShoppingCart className="w-5 h-5 text-emerald-700" />
              </Button>
              {cartCount > 0 && (
                <Badge className="absolute -top-2 -right-2 bg-emerald-600 hover:bg-emerald-600 text-white text-xs px-1.5 min-w-[20px] h-5 flex items-center justify-center">
                  {cartCount}
                </Badge>
              )}
            </Link>

            {/* Mobile menu button */}
            <Button
              variant="outline"
              size="icon"
              className="lg:hidden border-emerald-200"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-emerald-100 pt-4">
            <div className="flex flex-col gap-1">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'text-gray-600 hover:bg-emerald-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="flex gap-2 mt-4 px-4">
              <a 
                href={companyInfo.social.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg text-sm"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
              <a 
                href={companyInfo.social.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-sky-500 text-white rounded-lg text-sm"
              >
                <Send className="w-4 h-4" />
                Telegram
              </a>
              <a 
                href={companyInfo.social.max}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-purple-500 text-white rounded-lg text-sm"
              >
                MAX
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
